import AbstractShape, { Dimensions, DimensionProperties } from "./AbstractShape";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import DimensionLine from "@/Models/Lines/DimensionLine";
import { makerjs } from "../../../../source/js/shared/rendering/makerjs";
import { isEmpty } from "lodash";

export class Barrel extends AbstractShape {

	constructor( dimensions?: object ) {
		super( 'barrel', dimensions );
	}

	getHeight(): Length {
		return this.cleanedPrimitives.height;
	}

	getLength(): Length {
		return this.getWidth();
	}

	getWidth(): Length {
		return this.cleanedPrimitives.width;
	}

	getSideHeight(): Length {
		return this.cleanedPrimitives.side_height;
	}

	getLargestHeight(): Length {
		const height = this.getHeight();
		const sideHeight = this.getSideHeight();
		return height.in_mm() > sideHeight.in_mm() ? height : sideHeight;
	}

	getDimensionProperties(): DimensionProperties {
		return { primary: 'width', secondary: 'height', tertiary: 'side_height' };
	}

	getDimensionLines(ratio = 1, focusedElement = '', forceRender = false): DimensionLine[] {
		let dimensionLines = [];
		if(focusedElement.includes("cutout_measurement") || forceRender) {
			dimensionLines = [
				new DimensionLine(
					[0, 0],
					[0, this.getLargestHeight().in_mm()],
					this.renderDimensionLabel('height', this.getLargestHeight().in_cm()),
					[-40, 0],
					ratio,
					this.getDimensionLineType('measurements-barrel-height', focusedElement, forceRender)
				),
				new DimensionLine(
					[0, 0],
					[this.getWidth().in_mm(), 0],
					this.renderDimensionLabel('width'),
					[0, -40],
					ratio,
					this.getDimensionLineType('measurements-barrel-width', focusedElement, forceRender)
				),
			];
		}

		if(focusedElement.includes("measurements-barrel-height")) {
			dimensionLines.push(
				new DimensionLine(
					[this.getWidth().divide(2).in_mm(), 0],
					[this.getWidth().divide(2).in_mm(), this.getHeight().in_mm()],
					this.renderDimensionLabel('height'),
					[0, 0],
					ratio,
					this.getDimensionLineType('measurements-barrel-height', focusedElement, forceRender)
				)
			);
		}

		if(focusedElement.includes("measurements-barrel-side_height")) {
			const offsetY = this.getLargestHeight().subtract(this.getSideHeight()).divide(2);

			dimensionLines.push(
				new DimensionLine(
					[0, offsetY.in_mm()],
					[0, offsetY.add(this.getSideHeight()).in_mm()],
					this.renderDimensionLabel('side_height'),
					[40, 0],
					ratio,
					this.getDimensionLineType('measurements-barrel-side_height', focusedElement, forceRender)
				),
				new DimensionLine(
					[this.getWidth().in_mm(), offsetY.in_mm()],
					[this.getWidth().in_mm(), offsetY.add(this.getSideHeight()).in_mm()],
					this.renderDimensionLabel('side_height'),
					[-40, 0],
					ratio,
					this.getDimensionLineType('measurements-barrel-side_height', focusedElement, forceRender)
				)
			);
		}

		return dimensionLines;
	}

	createModel(): void {
		if(isEmpty(this.cleanedPrimitives)) {
			return;
		}

		super.createModel();
		makerjs.model.zero(this.model);
		makerjs.model.originate(this.model);
	}

	getDefaultDimensions(): Dimensions {
		return {
			width: 70,
			height: 40,
			side_height: 20,
		};
	}

	getDefaultCutoutDimensions(parentShape: AbstractShape): Dimensions {
		const enclosingRectangle = parentShape.getEnclosingRectangle();
		const height = enclosingRectangle.height();
		const width = enclosingRectangle.width();

		return {
			width: width.divide(2).in_cm(),
			height: height.divide(2).in_cm(),
			side_height: height.divide(4).in_cm(),
		};
	}
}
